import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  CloudDownload as CloudDownloadIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import CronBuilderBox from "../../../components/CronBuilderBox";
import CronStatusPanel from "../../../components/CronStatusPanel";
import { profitStatisticsApi } from "../../../services/api/omni/statistics";

const ALL_MARKETPLACES = ["DE", "FR", "PL", "SE", "NL", "BE", "ES", "IT"];

const ProfitTab = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split("T")[0];

  // Amazon sync state
  const [amazonSyncing, setAmazonSyncing] = useState(false);
  const [amazonReturnsSyncing, setAmazonReturnsSyncing] = useState(false);
  const [selectedMarketplaces, setSelectedMarketplaces] = useState([
    ...ALL_MARKETPLACES,
  ]);
  const [amazonDateRange, setAmazonDateRange] = useState({
    start_date: yesterdayStr,
    end_date: yesterdayStr,
  });
  const [amazonReturnsDateRange, setAmazonReturnsDateRange] = useState({
    start_date: yesterdayStr,
    end_date: yesterdayStr,
  });
  const [calculateFees, setCalculateFees] = useState(true);

  const toggleMarketplace = (code) => {
    setSelectedMarketplaces((prev) =>
      prev.includes(code) ? prev.filter((m) => m !== code) : [...prev, code],
    );
  };

  const handleAmazonSync = async () => {
    try {
      setAmazonSyncing(true);
      setError(null);
      const response = await profitStatisticsApi.syncAmazonOrders(
        amazonDateRange.start_date,
        amazonDateRange.end_date,
        selectedMarketplaces,
        { calculateFees },
      );
      const {
        total_records_stored,
        total_lines_fetched,
        marketplaces,
        batch_count,
        calculate_fees,
      } = response.data ?? {};
      setSuccess(
        `Amazon import complete: ${total_records_stored ?? "?"} rows stored ` +
          `(${total_lines_fetched ?? "?"} fetched` +
          (batch_count != null ? `, ${batch_count} batch(es)` : "") +
          `) for ` +
          `[${(marketplaces ?? selectedMarketplaces).join(", ")}] ` +
          `${amazonDateRange.start_date} → ${amazonDateRange.end_date}` +
          (calculate_fees === false
            ? " (fees/profits skipped)."
            : " (with fees/profits)."),
      );
    } catch (err) {
      setError(
        "Amazon import failed: " + (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setAmazonSyncing(false);
    }
  };

  const handleAmazonReturnsSync = async () => {
    try {
      setAmazonReturnsSyncing(true);
      setError(null);
      const response = await profitStatisticsApi.syncAmazonReturns(
        amazonReturnsDateRange.start_date,
        amazonReturnsDateRange.end_date,
        selectedMarketplaces,
      );
      const { rows_stored, batch_count, orders_marked, marketplaces, errors } =
        response.data ?? {};
      let msg =
        `Amazon returns import complete: ${rows_stored ?? "?"} rows stored ` +
        `across ${batch_count ?? "?"} batch(es) (≤30 days each); ` +
        `${orders_marked ?? "?"} order line(s) marked has_retour. ` +
        `[${(marketplaces ?? selectedMarketplaces).join(", ")}] ` +
        `${amazonReturnsDateRange.start_date} → ${amazonReturnsDateRange.end_date}.`;
      if (errors?.length) {
        msg += ` Partial errors: ${errors.length}.`;
      }
      setSuccess(msg);
    } catch (err) {
      setError(
        "Amazon returns import failed: " +
          (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setAmazonReturnsSyncing(false);
    }
  };

  const [cronSettings, setCronSettings] = useState({
    cron_expression: "0 6 * * *",
    is_cron_active: false,
    last_run: null,
    next_run: null,
  });

  const [dateRange, setDateRange] = useState({
    start_date: yesterdayStr,
    end_date: yesterdayStr,
  });

  const [retourDateRange, setRetourDateRange] = useState({
    start_date: yesterdayStr,
    end_date: yesterdayStr,
  });
  const [retourSyncing, setRetourSyncing] = useState(false);

  useEffect(() => {
    loadCronStatus();
  }, []);

  const loadCronStatus = async () => {
    try {
      const response = await profitStatisticsApi.getCronStatus();
      setCronSettings((prev) => ({
        ...prev,
        is_cron_active: response.data.active,
        cron_expression: response.data.expression || prev.cron_expression,
        last_run: response.data.last_run_at,
        next_run: response.data.next_run_at,
      }));
    } catch (err) {
      console.error("Failed to load profit cron status:", err);
    }
  };

  const handleStartCron = async () => {
    try {
      setLoading(true);
      setError(null);
      await profitStatisticsApi.startCron(cronSettings.cron_expression);
      setSuccess(
        `Cron started / updated with schedule: ${cronSettings.cron_expression}`,
      );
      await loadCronStatus();
    } catch (err) {
      setError(
        "Failed to start/update cron: " +
          (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStopCron = async () => {
    try {
      setLoading(true);
      setError(null);
      await profitStatisticsApi.stopCron();
      setSuccess("Cron stopped successfully.");
      await loadCronStatus();
    } catch (err) {
      setError(
        "Failed to stop cron: " + (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStoreRange = async () => {
    try {
      setSyncing(true);
      setError(null);
      const response = await profitStatisticsApi.storeOrdersForDateRange(
        dateRange.start_date,
        dateRange.end_date,
      );
      const totalLines = response.data?.reduce(
        (sum, day) => sum + (day.total_lines ?? 0),
        0,
      );
      setSuccess(
        `Imported ${totalLines ?? "?"} order lines for ${
          dateRange.start_date
        } → ${dateRange.end_date}.`,
      );
    } catch (err) {
      setError(
        "Failed to import orders: " +
          (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleStoreRetoursRange = async () => {
    try {
      setRetourSyncing(true);
      setError(null);
      const response = await profitStatisticsApi.storeRetoursForDateRange(
        retourDateRange.start_date,
        retourDateRange.end_date,
      );
      const totalLines = response.data?.reduce(
        (sum, day) => sum + (day.total_lines ?? 0),
        0,
      );
      const fetched = response.data?.reduce(
        (sum, day) => sum + (day.parent_orders_fetched ?? 0),
        0,
      );
      const marked = response.data?.reduce(
        (sum, day) => sum + (day.parent_orders_marked ?? 0),
        0,
      );
      const missing = response.data?.reduce(
        (sum, day) => sum + (day.parent_orders_missing ?? 0),
        0,
      );
      setSuccess(
        `Imported ${totalLines ?? "?"} retour lines for ${
          retourDateRange.start_date
        } → ${retourDateRange.end_date}; ` +
          `backfilled ${fetched ?? "?"} parent order(s), marked ${
            marked ?? "?"
          } with retour` +
          (missing ? ` (${missing} parent order(s) still missing)` : "") +
          ".",
      );
    } catch (err) {
      setError(
        "Failed to import retours: " +
          (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setRetourSyncing(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* ── Cron Control Panel ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Header row */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6">
              Plenty &amp; Amazon Orders — Schedule
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadCronStatus}
                disabled={loading}
              >
                Refresh
              </Button>
              <Chip
                icon={
                  cronSettings.is_cron_active ? (
                    <CheckCircleIcon />
                  ) : (
                    <StopIcon />
                  )
                }
                label={cronSettings.is_cron_active ? "Running" : "Stopped"}
                color={cronSettings.is_cron_active ? "success" : "error"}
                variant="outlined"
              />
            </Box>
          </Box>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Daily cron fetches yesterday&apos;s Plenty orders and retours, then
            syncs Amazon FBA orders and customer returns.
          </Typography>

          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Cron generator */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Cron Schedule
              </Typography>
              <CronBuilderBox mb={2}>
                <Cron
                  onChange={(e) =>
                    setCronSettings((prev) => ({
                      ...prev,
                      cron_expression: e,
                    }))
                  }
                  value={cronSettings.cron_expression}
                  showResultText={true}
                  showResultCron={true}
                  locale="en"
                />
              </CronBuilderBox>
            </Box>

            {/* Action buttons */}
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayIcon />
                  )
                }
                onClick={handleStartCron}
                disabled={loading}
              >
                Start / Update Schedule
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleStopCron}
                disabled={loading || !cronSettings.is_cron_active}
              >
                Stop Schedule
              </Button>
            </Box>

            <CronStatusPanel isActive={cronSettings.is_cron_active}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Cron Status
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {cronSettings.is_cron_active ? (
                      <Box
                        component="span"
                        sx={{
                          color: "success.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <CheckCircleIcon fontSize="small" /> Active
                      </Box>
                    ) : (
                      <Box
                        component="span"
                        sx={{
                          color: "error.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <StopIcon fontSize="small" /> Inactive
                      </Box>
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Schedule
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {cronSettings.cron_expression || "Not set"}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Last Run
                  </Typography>
                  <Typography variant="body1">
                    {cronSettings.last_run
                      ? new Date(cronSettings.last_run).toLocaleString("de-DE")
                      : "Never"}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Next Execution
                  </Typography>
                  <Typography
                    variant="body1"
                    color={
                      cronSettings.is_cron_active
                        ? "textPrimary"
                        : "textSecondary"
                    }
                  >
                    {cronSettings.next_run && cronSettings.is_cron_active
                      ? new Date(cronSettings.next_run).toLocaleString("de-DE")
                      : "—"}
                  </Typography>
                </Box>
              </Box>
            </CronStatusPanel>
          </Box>
        </CardContent>
      </Card>

      {/* ── Manual Date Range Import ── */}
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Manual Plenty Orders Import
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Fetch and store Plenty orders for a specific date range. The cron
            job runs daily for yesterday automatically (Plenty orders, retours,
            Amazon orders, and Amazon returns) — use this to backfill historical
            Plenty order data.
          </Typography>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={dateRange.start_date}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  start_date: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={dateRange.end_date}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, end_date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              color="secondary"
              startIcon={
                syncing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DateRangeIcon />
                )
              }
              onClick={handleStoreRange}
              disabled={syncing || !dateRange.start_date || !dateRange.end_date}
            >
              {syncing ? "Importing…" : "Import Range"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Manual Plenty Returns Import ── */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Manual Plenty Returns Import
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Fetch and store Plenty return orders (orderTypeId=4) for a date
            range. Parent sales orders are marked with has_retour=true. The cron
            job also fetches yesterday&apos;s retours automatically.
          </Typography>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={retourDateRange.start_date}
              onChange={(e) =>
                setRetourDateRange((prev) => ({
                  ...prev,
                  start_date: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={retourDateRange.end_date}
              onChange={(e) =>
                setRetourDateRange((prev) => ({
                  ...prev,
                  end_date: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              color="secondary"
              startIcon={
                retourSyncing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DateRangeIcon />
                )
              }
              onClick={handleStoreRetoursRange}
              disabled={
                retourSyncing ||
                !retourDateRange.start_date ||
                !retourDateRange.end_date
              }
            >
              {retourSyncing ? "Importing…" : "Import Retours"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Manual Amazon Orders Import ── */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            Manual Amazon FBA Orders Import
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Fetch and store Amazon FBA order lines for a specific date range and
            marketplace selection. The cron job automatically fetches Plenty
            orders/retours and Amazon orders/returns for yesterday — use this to
            backfill historical Amazon order data.
          </Typography>

          {/* Marketplace toggle chips */}
          <Typography variant="subtitle2" gutterBottom>
            Marketplaces
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {ALL_MARKETPLACES.map((code) => (
              <Chip
                key={code}
                label={code}
                onClick={() => toggleMarketplace(code)}
                color={
                  selectedMarketplaces.includes(code) ? "primary" : "default"
                }
                variant={
                  selectedMarketplaces.includes(code) ? "filled" : "outlined"
                }
                clickable
              />
            ))}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={calculateFees}
                onChange={(e) => setCalculateFees(e.target.checked)}
                color="primary"
              />
            }
            label="Calculate fees & profits (ProductFees + buy price)"
            sx={{ mb: 2, display: "block" }}
          />
          {!calculateFees && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={2}
            >
              Faster import: stores order lines without fee estimates or profit.
              Re-import later with this enabled to backfill economics.
            </Typography>
          )}

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={amazonDateRange.start_date}
              onChange={(e) =>
                setAmazonDateRange((prev) => ({
                  ...prev,
                  start_date: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={amazonDateRange.end_date}
              onChange={(e) =>
                setAmazonDateRange((prev) => ({
                  ...prev,
                  end_date: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                amazonSyncing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudDownloadIcon />
                )
              }
              onClick={handleAmazonSync}
              disabled={
                amazonSyncing ||
                !amazonDateRange.start_date ||
                !amazonDateRange.end_date ||
                selectedMarketplaces.length === 0
              }
            >
              {amazonSyncing ? "Importing…" : "Import Amazon Orders"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Manual Amazon FBA Returns Import ── */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            Manual Amazon FBA Returns Import
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Fetch and store FBA customer returns (
            GET_FBA_FULFILLMENT_CUSTOMER_RETURNS_DATA). Ranges longer than 30
            days are split into report batches automatically. Matching{" "}
            <code>amazon_orders</code> rows get <code>has_retour=true</code>.
            The daily cron imports yesterday only.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Marketplaces
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {ALL_MARKETPLACES.map((code) => (
              <Chip
                key={`ret-${code}`}
                label={code}
                onClick={() => toggleMarketplace(code)}
                color={
                  selectedMarketplaces.includes(code) ? "primary" : "default"
                }
                variant={
                  selectedMarketplaces.includes(code) ? "filled" : "outlined"
                }
                clickable
              />
            ))}
          </Box>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={amazonReturnsDateRange.start_date}
              onChange={(e) =>
                setAmazonReturnsDateRange((prev) => ({
                  ...prev,
                  start_date: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={amazonReturnsDateRange.end_date}
              onChange={(e) =>
                setAmazonReturnsDateRange((prev) => ({
                  ...prev,
                  end_date: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                amazonReturnsSyncing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudDownloadIcon />
                )
              }
              onClick={handleAmazonReturnsSync}
              disabled={
                amazonReturnsSyncing ||
                !amazonReturnsDateRange.start_date ||
                !amazonReturnsDateRange.end_date ||
                selectedMarketplaces.length === 0
              }
            >
              {amazonReturnsSyncing ? "Importing…" : "Import Amazon Returns"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfitTab;
